import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from 'styled-components';
import Newsroom from "../";
import Row from "../../Bootstrap/Row";
import Loader from "../../Loader";
import { AutomationPlacement, AutomationTestIdElementType, AutomationTestIdFeature, createDataTestId } from "../../../utilities/automation";
import HttpService from "../../../services/HttpService";
import { Search } from '../../../..';

interface INewsroomResultsTitleProps {
    count: number;
    children: string | React.ReactChildren;
}

function NewsroomResultsTitle({ children }: INewsroomResultsTitleProps) {
    return (
        <div className="col-12 d-flex align-items-baseline">
            <h2 className="text-primary mx-auto mb-3 mt-6 text-sans" data-test-id={createDataTestId({placement: AutomationPlacement.NEWSROOM_ARCHIVE_PAGE, feature: AutomationTestIdFeature.NEWSROOM_SEARCH, element: AutomationTestIdElementType.TEXT, specifier: 'Title'})}>{children}</h2>
        </div>
    )
}

interface INewsroomResultsProps {
    title: string;
    limit: number;
}

const NewsroomResultContainer = styled.div`
    .newsroom-result-row {
        display:block;
    }
`;


export function NewsroomResults(props: INewsroomResultsProps) {
    const [posts, setPosts] = useState([]);
    const [page, setPage] = useState(1);
    const [isSearching, setIsSearching] = useState(false);
    const [totalArticleCount, setTotalArticleCount] = useState(0);
    const [lastPage, setLastPage] = useState(false);

    const loadMore = useCallback(() => {
        setPage(page + 1);
    }, [page]);

    useEffect(() => {
        async function loadResults() {
            try {
                setIsSearching(true);
                const res = await HttpService.get<any>(process.env.GATSBY_SOLR_SEARCH_URL, null, {
                    params: {
                      q: "", // q is required even if blank
                      pt: "newsroom",
                      page,
                      limit: props.limit,
                      clientCacheBypass: `${Math.floor(Date.now() / parseInt(process.env.SOLR_CACHE_INTERVAL_MS || '900001'))}`, // create a param to bypass the cache every 15 minutes
                    }
                  }
                );
                setTotalArticleCount(res.response.numFound);
                if (res.response.start + props.limit >= res.response.numFound) { // Checks if last apge
                    setLastPage(true)
                }
                if (res.response.docs.length > 0) {
                    const additionalArticles = res.response.docs;
                    setPosts([...posts, ...additionalArticles]);
                }
                setIsSearching(false);
            } catch (err) {
                console.error(err);
                setIsSearching(false);
            }
        }
        loadResults();
    }, [page]);

    return (
        <NewsroomResultContainer className="w-100">
            <NewsroomResultsTitle count={totalArticleCount}>{props.title}</NewsroomResultsTitle>
            <div className="row">
                <div className="offset-lg-1 col-lg-10 my-6 p-0">
                    <Search
                        placement={AutomationPlacement.NEWSROOM_ARCHIVE_PAGE}
                        inputPlaceholder="Search articles..."
                        pageType="newsroom" />
                </div>
            </div>
            {posts.length ? (<Row className="newsroom-result-row" data-test-id={createDataTestId({placement: AutomationPlacement.NEWSROOM_ARCHIVE_PAGE, feature: AutomationTestIdFeature.NEWSROOM_SEARCH, element: AutomationTestIdElementType.COLLECTION, specifier: 'Results'})}>
                {posts.map(post => {
                    return (
                        <div data-test-id={createDataTestId({placement: AutomationPlacement.NEWSROOM_ARCHIVE_PAGE, feature: AutomationTestIdFeature.NEWSROOM_SEARCH, element: AutomationTestIdElementType.COLLECTION_ITEM, specifier: 'Results'})}
                            key={post.id}
                            className="newsroom-results-item">
                            <Newsroom
                                headerAs='small'
                                href={post.path?.[0]}
                                title={post.blog_title?.[0]}
                                description={post.description?.[0]}
                                content={post.content?.[0]}
                                timestamp={post.timestamp}
                                alt={post.thumb_image_alt?.[0]}
                                src={post.thumb_image_src?.[0] || post.image_src?.[0]}
                                dateType="date"
                            />
                        </div>
                    );
                })} </Row>)
                :
                isSearching ? null : <div className="offset-lg-1 col-lg-10">No articles found</div>
            }
            {isSearching ?
                <Row className="position-relative w-100 justify-content-center mb-4">
                    <h4 className="text-muted font-weight-light">LOADING MORE</h4>
                    <Loader spinner={isSearching} top={40} width={4} height={4} variant="secondary" />
                </Row>
            :
                !lastPage &&
                    <Row className="position-relative w-100 justify-content-center mb-4">
                        <button className="btn btn-primary" onClick={loadMore}>LOAD MORE</button>
                    </Row>

            }
        </NewsroomResultContainer>
    );
}